/**
 * Decrypt invitation `data` parameter (client-side only).
 *
 * Key source:
 *   - REACT_APP_ENCRYPTION_KEY env var (build-time, optional local fallback)
 *
 * Decryption strategies (tried in order):
 *   1. Direct JSON parse
 *   2. Base64url decode → JSON parse
 *   3. AES-CBC (16-byte IV prefix)
 *   4. AES-GCM (12-byte nonce prefix)
 *   5. AES-GCM (16-byte nonce prefix)
 */

import { getApiOrigin } from "@utils/apiOrigin";

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

function decodeKeyB64(keyB64: string): Uint8Array | null {
  if (!keyB64) return null;
  try {
    let b64 = keyB64.trim().replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    if (bytes.length !== 32) return null;
    return bytes;
  } catch {
    return null;
  }
}

function getEnvKey(): Uint8Array | null {
  const envKey = (
    (process.env.REACT_APP_ENCRYPTION_KEY as string) || ""
  ).trim();
  return decodeKeyB64(envKey);
}

async function decryptViaRulesApi(
  encryptedString: string,
): Promise<Record<string, any> | null> {
  const baseUrl = getApiOrigin() || "";
  if (!baseUrl) return null;

  try {
    const response = await fetch(`${baseUrl}/api/v1/user-management/decrypt-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ encrypted_data: encryptedString }),
    });
    if (!response.ok) return null;

    const text = await response.text();
    const json = text ? (JSON.parse(text) as any) : null;
    if (!json?.success || !json?.data || typeof json.data !== "object") return null;
    return json.data as Record<string, any>;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// SimpleDecryption class
// ---------------------------------------------------------------------------

class SimpleDecryption {
  private keyBytes: Uint8Array | null = null;
  private keyResolved = false;

  private resolveKey(): Uint8Array | null {
    if (!this.keyResolved) {
      this.keyBytes = getEnvKey();
      this.keyResolved = true;
    }
    return this.keyBytes;
  }

  private base64UrlDecode(str: string): Uint8Array {
    let padded = (str || "").replace(/-/g, "+").replace(/_/g, "/");
    while (padded.length % 4) padded += "=";
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private tryParseJson(text: string): Record<string, any> | null {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // ignore
    }
    return null;
  }

  private toArrayBuffer(arr: Uint8Array): ArrayBuffer {
    return arr.buffer.slice(
      arr.byteOffset,
      arr.byteOffset + arr.byteLength,
    ) as ArrayBuffer;
  }

  private async tryAesDecrypt(
    encryptedBytes: Uint8Array,
    rawKey: Uint8Array,
  ): Promise<Record<string, any> | null> {
    if (!window?.crypto?.subtle) return null;
    const keyBuf = this.toArrayBuffer(rawKey);

    // AES-CBC: 16-byte IV prefix
    if (encryptedBytes.length > 16) {
      try {
        const key = await window.crypto.subtle.importKey(
          "raw",
          keyBuf,
          { name: "AES-CBC" },
          false,
          ["decrypt"],
        );
        const iv = this.toArrayBuffer(encryptedBytes.slice(0, 16));
        const ct = this.toArrayBuffer(encryptedBytes.slice(16));
        const plain = await window.crypto.subtle.decrypt(
          { name: "AES-CBC", iv },
          key,
          ct,
        );
        const j = this.tryParseJson(new TextDecoder().decode(plain));
        if (j) return j;
      } catch {
        // next variant
      }
    }

    // AES-GCM: 12-byte nonce prefix
    if (encryptedBytes.length > 28) {
      try {
        const key = await window.crypto.subtle.importKey(
          "raw",
          keyBuf,
          { name: "AES-GCM" },
          false,
          ["decrypt"],
        );
        const iv = this.toArrayBuffer(encryptedBytes.slice(0, 12));
        const ct = this.toArrayBuffer(encryptedBytes.slice(12));
        const plain = await window.crypto.subtle.decrypt(
          { name: "AES-GCM", iv, tagLength: 128 },
          key,
          ct,
        );
        const j = this.tryParseJson(new TextDecoder().decode(plain));
        if (j) return j;
      } catch {
        // next variant
      }
    }

    // AES-GCM: 16-byte nonce prefix
    if (encryptedBytes.length > 32) {
      try {
        const key = await window.crypto.subtle.importKey(
          "raw",
          keyBuf,
          { name: "AES-GCM" },
          false,
          ["decrypt"],
        );
        const iv = this.toArrayBuffer(encryptedBytes.slice(0, 16));
        const ct = this.toArrayBuffer(encryptedBytes.slice(16));
        const plain = await window.crypto.subtle.decrypt(
          { name: "AES-GCM", iv, tagLength: 128 },
          key,
          ct,
        );
        const j = this.tryParseJson(new TextDecoder().decode(plain));
        if (j) return j;
      } catch {
        // no-op
      }
    }

    return null;
  }

  async decrypt(encryptedString: string): Promise<Record<string, any>> {
    const apiDecrypted = await decryptViaRulesApi(encryptedString);
    if (apiDecrypted) return apiDecrypted;

    const direct = this.tryParseJson(encryptedString);
    if (direct) return direct;

    const encryptedBytes = this.base64UrlDecode(encryptedString);

    const plainJson = this.tryParseJson(
      new TextDecoder().decode(encryptedBytes),
    );
    if (plainJson) return plainJson;

    const rawKey = this.resolveKey();
    if (!rawKey) {
      throw new Error("Encryption key unavailable");
    }

    const aesJson = await this.tryAesDecrypt(encryptedBytes, rawKey);
    if (aesJson) return aesJson;

    throw new Error("Failed to decrypt data");
  }
}

const decryption = new SimpleDecryption();

export const decryptInvitationParams = async (
  encryptedData: string,
): Promise<Record<string, any>> => {
  if (!encryptedData) {
    throw new Error("No invitation data provided");
  }

  try {
    return await decryption.decrypt(encryptedData);
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.error("Error decrypting invitation parameters:", error);
    throw new Error(
      `Failed to decrypt invitation: ${error?.message || "Unknown error"}`,
    );
  }
};
